import { FC, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

import FullAccessBanner from '../../../common/FullAccessBanner';

import AccountInfo from './components/AccountInfo';
import Personalization from './components/Personalization/Personalization';
import PassSettings from './components/PassSettings';
import GeneralSettings from './components/GeneralSettings';
import Subscription from './components/Subscription';
import EmergencyContacts from './components/EmergencyContacts';
import TravelBuddies from './components/TravelBuddies';
import ReferralProgram from './components/ReferralProgram';
import { useAppSelector } from '../../../hooks/redux';

import styles from './Profile.module.scss';

const PROFILE_TABS = ['personal-info', 'contacts', 'subscription', 'settings', 'referral'] as const;

const Profile: FC = () => {
  const [searchParams] = useSearchParams();
  const [selected, setSelected] = useState('personal-info');
  const user = useAppSelector(state => state.user);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && PROFILE_TABS.includes(tab as (typeof PROFILE_TABS)[number])) {
      setSelected(tab);
    }
  }, [searchParams]);

  const handleToggleChange = (_: React.MouseEvent<HTMLElement>, newSelection: string | null) => {
    if (newSelection !== null) setSelected(newSelection);
  };

  return (
    <Box className={styles.personalInfo}>
      <Typography
        variant="h4Bold"
        className={styles.pageTitle}
      >
        Profile
      </Typography>
      <Box className={styles.tabsScroll}>
        <ToggleButtonGroup
          value={selected}
          exclusive
          onChange={handleToggleChange}
          className={styles.toggleGroup}
        >
          <ToggleButton
            value="personal-info"
            className={`${selected === 'personal-info' && styles.selected} ${styles.toggleButton}`}
          >
            {selected === 'personal-info' ? (
              <Typography variant="h10Bold">Personal info</Typography>
            ) : (
              <Typography variant="h10Semibold">Personal info</Typography>
            )}
          </ToggleButton>
          <ToggleButton
            value="contacts"
            className={`${selected === 'contacts' && styles.selected} ${styles.toggleButton}`}
          >
            {selected === 'contacts' ? (
              <Typography variant="h10Bold">Contacts</Typography>
            ) : (
              <Typography variant="h10Semibold">Contacts</Typography>
            )}
          </ToggleButton>
          <ToggleButton
            value="subscription"
            className={`${selected === 'subscription' && styles.selected} ${styles.toggleButton}`}
          >
            {selected === 'subscription' ? (
              <Typography variant="h10Bold">Subscription</Typography>
            ) : (
              <Typography variant="h10Semibold">Subscription</Typography>
            )}
          </ToggleButton>
          <ToggleButton
            value="settings"
            className={`${selected === 'settings' && styles.selected} ${styles.toggleButton}`}
          >
            {selected === 'settings' ? (
              <Typography variant="h10Bold">Settings</Typography>
            ) : (
              <Typography variant="h10Semibold">Settings</Typography>
            )}
          </ToggleButton>
          {user.referral_program_eligible && (
            <ToggleButton
              value="referral"
              className={`${selected === 'referral' && styles.selected} ${styles.toggleButton}`}
            >
              {selected === 'referral' ? (
                <Typography variant="h10Bold">Referral program</Typography>
              ) : (
                <Typography variant="h10Semibold">Referral program</Typography>
              )}
            </ToggleButton>
          )}
        </ToggleButtonGroup>
      </Box>

      {selected === 'personal-info' && (
        <Box className={styles.contentWrapper}>
          <Box className={styles.content}>
            <AccountInfo />
            <Box className={styles.personalizationWrapper}>
              <Personalization />
              {!user.is_premium && (
                <FullAccessBanner
                  variant="overlay"
                  onGetFullAccess={() => setSelected('subscription')}
                />
              )}
            </Box>
          </Box>
        </Box>
      )}

      {selected === 'contacts' && (
        <Box className={styles.contentWrapper}>
          <Box className={styles.content}>
            <EmergencyContacts />
            <TravelBuddies />
          </Box>
        </Box>
      )}

      {selected === 'settings' && (
        <Box className={`${styles.contentWrapper} ${styles.settingsRow}`}>
          {!user.is_google_user && <PassSettings />}
          <GeneralSettings />
        </Box>
      )}

      {selected === 'subscription' && (
        <Box className={styles.contentWrapper}>
          <Subscription />
        </Box>
      )}

      {selected === 'referral' && user.referral_program_eligible && (
        <Box className={styles.contentWrapper}>
          <ReferralProgram />
        </Box>
      )}
    </Box>
  );
};

export default Profile;
